import React, { useState, useEffect } from 'react';
import api from '../api';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { TrendingUp, TrendingDown, Euro, Receipt } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [yearlyStats, setYearlyStats] = useState(null);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeData();
    fetchDashboardData();
  }, []);

  const initializeData = async () => {
    try {
      await api.post('/init-data');
    } catch (error) {
      console.log('Dati già inizializzati');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;

      const [monthlyRes, yearlyRes, expensesRes] = await Promise.all([
        api.get(`/stats/monthly?year=${year}&month=${month}`),
        api.get(`/stats/yearly?year=${year}`),
        api.get('/expenses'),
      ]);

      setMonthlyStats(monthlyRes.data);
      setYearlyStats(yearlyRes.data);
      setRecentExpenses(expensesRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-600">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Dashboard
        </h2>
        <p className="text-slate-600">Panoramica delle tue spese familiari</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6" data-testid="monthly-total-card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-[#FF6B6B]/10 rounded-lg">
              <Euro className="w-6 h-6 text-[#FF6B6B]" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-slate-600 mb-1">Spesa Mensile</h3>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {formatCurrency(monthlyStats?.total || 0)}
          </p>
          <p className="text-sm text-slate-500 mt-2">{monthlyStats?.count || 0} transazioni</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6" data-testid="yearly-total-card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-[#4ECDC4]/10 rounded-lg">
              <TrendingUp className="w-6 h-6 text-[#4ECDC4]" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-slate-600 mb-1">Spesa Annuale</h3>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {formatCurrency(yearlyStats?.total || 0)}
          </p>
          <p className="text-sm text-slate-500 mt-2">{yearlyStats?.count || 0} transazioni</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6" data-testid="categories-card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-[#45B7D1]/10 rounded-lg">
              <Receipt className="w-6 h-6 text-[#45B7D1]" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-slate-600 mb-1">Categorie Attive</h3>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {monthlyStats?.by_category?.length || 0}
          </p>
          <p className="text-sm text-slate-500 mt-2">Questo mese</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Chart */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6" data-testid="monthly-chart">
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
            Spese Mensili per Categoria
          </h3>
          {monthlyStats?.by_category && monthlyStats.by_category.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={monthlyStats.by_category}
                  dataKey="amount"
                  nameKey="category_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.category_name}: ${formatCurrency(entry.amount)}`}
                >
                  {monthlyStats.by_category.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-slate-400">
              Nessuna spesa registrata questo mese
            </div>
          )}
        </div>

        {/* Yearly Chart */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6" data-testid="yearly-chart">
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
            Spese Annuali per Categoria
          </h3>
          {yearlyStats?.by_category && yearlyStats.by_category.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={yearlyStats.by_category}
                  dataKey="amount"
                  nameKey="category_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.category_name}: ${formatCurrency(entry.amount)}`}
                >
                  {yearlyStats.by_category.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-slate-400">
              Nessuna spesa registrata quest'anno
            </div>
          )}
        </div>
      </div>

      {/* Recent Expenses */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6" data-testid="recent-expenses">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
          Ultime Transazioni
        </h3>
        {recentExpenses.length > 0 ? (
          <div className="space-y-3">
            {recentExpenses.map((expense) => (
              <div
                key={expense.id}
                className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors"
              >
                <div>
                  <p className="font-medium text-slate-900">{formatCurrency(expense.amount)}</p>
                  <p className="text-sm text-slate-500">{formatDate(expense.date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">{expense.notes || 'Nessuna nota'}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-slate-400 py-8">Nessuna transazione recente</div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
